// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import categoryAPI from '../../api/services/budget/categoryService';
import { isNetworkError } from '../../offline/outbox';
import { cacheCategories, getCachedCategories } from '../../offline/db';

export default function CategorySelect({
  value,
  onChange,
  categoryType = 'EXPENSE',
  disabled = false,
  placeholder = 'Select a category...',
  error = '',
  allowCreate = true,
}) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await categoryAPI.list({
        type: categoryType,
        active: 'true',
      });
      const cats = response.data || [];
      setCategories(cats);
      await cacheCategories(cats);
    } catch (err) {
      if (isNetworkError(err)) {
        // Offline (#22): without this, the dropdown renders empty and
        // blocks adding/editing a transaction at all while offline.
        setCategories(await getCachedCategories({ type: categoryType }));
      } else {
        console.error('Failed to load categories:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [categoryType]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const selectedCategory = categories.find((c) => c.id === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled || loading}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${disabled ? 'bg-gray-100' : 'bg-white'}`}
      >
        {/* text-sm here (not text-lg like the open dropdown's rows, which
            have more room) so this closed box matches a plain <select>'s
            height — it sits next to Payment Mode's select in
            TransactionForm and used to render visibly taller (#21). */}
        <span className={`flex items-center gap-2 ${selectedCategory ? 'text-gray-900' : 'text-gray-500'}`}>
          {selectedCategory?.icon && <span className="text-sm">{selectedCategory.icon}</span>}
          {loading ? 'Loading...' : selectedCategory?.name || placeholder}
        </span>
        <ChevronDown size={16} className="text-gray-400" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
          <div className="max-h-48 overflow-y-auto">
            {categories.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                {loading ? 'Loading...' : 'No categories available'}
              </div>
            ) : (
              categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => {
                    onChange(category.id);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-100 transition-colors ${
                    value === category.id ? 'bg-indigo-50' : ''
                  }`}
                >
                  {category.icon ? (
                    <span className="text-lg">{category.icon}</span>
                  ) : (
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                  )}
                  <span className="text-sm text-gray-900">{category.name}</span>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          {allowCreate && (
            <div className="border-t px-3 py-2">
              <a
                href="/apps/categories"
                className="text-xs text-indigo-600 hover:text-indigo-700"
              >
                + Add new category
              </a>
            </div>
          )}
        </div>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}
